import React, { useState, useEffect, useRef } from 'react';
import { Crosshair, MapPin, Loader2, X, Check } from 'lucide-react';
import placesService from '../../services/placesService';

/**
 * GoogleCoordinatesAutocomplete
 * Special input for Google Coordinates:
 * Allows user to type coordinates directly (e.g. 23.0225, 72.5714)
 * OR search any place/landmark (e.g. "Rajkot", "Shalby Hospital")
 * and upon selection, automatically fills ONLY the coordinates.
 */
export const GoogleCoordinatesAutocomplete = ({
  value = '',
  onChange,
  placeholder = 'Search place or enter coordinates',
  className = ''
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [justSelected, setJustSelected] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Sync external value changes (e.g. initial draft load)
  useEffect(() => {
    if (value !== inputValue && !showDropdown) {
      setInputValue(value || '');
    }
  }, [value]);

  // Click outside listener to close suggestions dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Helper to check if string looks like numeric coordinates (e.g., "23.02, 72.57" or pure numbers/dots)
  const isCoordinatesPattern = (text) => {
    const trimmed = text.trim();
    if (!trimmed) return false;
    // If it only contains digits, dots, commas, minus, plus, spaces, it's coordinates input
    return /^[-\d.,\s+]+$/.test(trimmed);
  };

  const handleInputChange = (e) => {
    const text = e.target.value;
    setInputValue(text);
    setJustSelected(false);

    // Call parent onChange directly so any manual coordinate edits are captured
    if (onChange) {
      onChange(text);
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // If user is typing raw coordinates or input is too short, don't show place suggestions
    if (text.trim().length < 2 || isCoordinatesPattern(text)) {
      setSuggestions([]);
      setShowDropdown(false);
      setLoading(false);
      return;
    }

    // Debounced Google Places autocomplete search
    debounceTimerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await placesService.getAutocomplete(text.trim(), { country: 'in' });
        if (response && response.success && Array.isArray(response.data) && response.data.length > 0) {
          setSuggestions(response.data);
          setShowDropdown(true);
        } else {
          setSuggestions([]);
          setShowDropdown(false);
        }
      } catch (err) {
        console.warn('Coordinates place autocomplete search error:', err);
        setSuggestions([]);
        setShowDropdown(false);
      } finally {
        setLoading(false);
      }
    }, 320);
  };

  const handleSelectPlace = async (suggestion) => {
    try {
      setLoading(true);
      setShowDropdown(false);

      // Fetch place details with exact coordinates from Google Places
      const response = await placesService.getPlaceDetails(suggestion.placeId);
      if (response && response.success && response.data) {
        const place = response.data;
        const lat =
          place.location?.latitude ??
          place.location?.lat;
        const lng =
          place.location?.longitude ??
          place.location?.lng;

        if (lat !== undefined && lng !== undefined) {
          // Format coordinates cleanly to 6 decimal places (approx. 10cm accuracy)
          const formattedCoords = `${Number(Number(lat).toFixed(6))}, ${Number(Number(lng).toFixed(6))}`;
          setInputValue(formattedCoords);
          setJustSelected(true);

          // ONLY update google_coordinates field, do not touch any other field
          if (onChange) {
            onChange(formattedCoords);
          }

          setTimeout(() => setJustSelected(false), 3000);
        }
      }
    } catch (err) {
      console.error('Failed to get coordinates for place:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setInputValue('');
    setSuggestions([]);
    setShowDropdown(false);
    setJustSelected(false);
    if (onChange) {
      onChange('');
    }
    inputRef.current?.focus();
  };

  return (
    <div className="relative w-full">
      <div className="relative">
        <Crosshair className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />

        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => {
            if (suggestions.length > 0 && !isCoordinatesPattern(inputValue)) {
              setShowDropdown(true);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setShowDropdown(false);
            }
          }}
          className={`w-full pl-10 pr-10 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${className}`}
        />

        {/* Right Action Icons (Loading, Just Selected checkmark, or Clear button) */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
          {loading && (
            <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
          )}

          {!loading && justSelected && (
            <span
              title="Coordinates auto-filled from selected place"
              className="inline-flex items-center text-emerald-600"
            >
              <Check className="w-4 h-4" />
            </span>
          )}

          {!loading && !justSelected && inputValue && (
            <button
              type="button"
              onClick={handleClear}
              className="text-slate-400 hover:text-slate-600 p-0.5 rounded cursor-pointer transition-colors"
              title="Clear coordinates"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Google Places Dropdown Suggestions */}
      {showDropdown && suggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute left-0 right-0 top-full mt-1.5 bg-white rounded-xl border border-slate-200 shadow-xl overflow-hidden z-50 animate-in fade-in-50 duration-150 max-h-64 overflow-y-auto"
        >
          <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
            <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">
              Google Places (Select to set coordinates)
            </span>
            <span className="text-[10px] text-blue-600 font-medium">Auto-fills Coordinates only</span>
          </div>

          <ul className="divide-y divide-slate-100">
            {suggestions.map((item) => (
              <li
                key={item.placeId || item.description}
                onClick={() => handleSelectPlace(item)}
                className="px-3.5 py-2.5 hover:bg-blue-50/70 cursor-pointer transition-colors flex items-start gap-2.5 group text-left"
              >
                <div className="w-6 h-6 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 shrink-0 mt-0.5 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                  <MapPin className="w-3.5 h-3.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-800 truncate group-hover:text-blue-700">
                    {item.mainText || item.description}
                  </p>
                  {item.secondaryText && (
                    <p className="text-[11px] text-slate-500 truncate mt-0.5">
                      {item.secondaryText}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default GoogleCoordinatesAutocomplete;
